import Grid from '@mui/material/Grid';
import ConfirmationDialog from 'components/AlertToAction';
import RMProductsDetailPanel from 'components/DetailPanels/RMProductsDetailPanel';
import ShipmentsDetailPanel from 'components/DetailPanels/ShipmentsDetailPanel';
import SubstancesDetailPanel from 'components/DetailPanels/SubstanceDetailPanel';
import BaseTable from 'components/Tables/BaseTable';
import { alertError } from 'ducks/alerts';
import { actions as rawMaterialsActions } from 'ducks/rawMaterials';
import { confirmBoxClose, confirmBoxOpen, setContextSearchRM } from 'ducks/ui';
import ConsumptionsDetailPanel from 'pages/Inventory/Consumptions/ConsumptionsDetailPanel';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import React from 'react';
import { connect } from 'react-redux';
import docDefinition from 'reports/inventory/rawMaterials';
import {
  filterByFunctionSelector, groupBy, mixtureSubstancesByMixture,
  mixtureSubstancesBySubstance, shipmentsByRawMaterial
} from 'selectors/generic';
import theme from 'source/theme';
import { MIXTURE } from 'utils/constants/coreConstants';
import exportXLSX from 'utils/export/rawMaterials';
import tableIcons from 'utils/icons/materialTableIcons';
import columns from 'utils/tables/columns/inventory/rawMaterials';
import noIcon from 'utils/tables/noIcon';
import rawMaterialValidator from 'utils/tables/validators/rawMaterial';
import { currentUser } from 'selectors/currentUser';

pdfMake.addVirtualFileSystem(pdfFonts);

const Component = props => {
  const {
    compositionByProduct, compositionByRawMaterial, consumptionsByRawMaterial,
    contextSearch, consumptionsById, filteredRawMaterials, mixturesBySubstance,
    products, productsById, rawMaterials, rawMaterialsById, shipmentsOfRawMaterial,
    substancesByMixture, suppliersById, ui, user
  } = props;
  const {
    alertError, confirmBoxOpen, confirmBoxClose, create, fetch, remove,
    setSearch, update
  } = props;

  const tableRef = React.useRef(null);

  const onUpdate = (newData, oldData) => {
    const validation = rawMaterialValidator(newData, rawMaterials);
    validation.cas = JSON.stringify(validation.cas);
    validation.ec = JSON.stringify(validation.ec);
    // validation.hazardous = JSON.stringify(validation.hazardous);
    if (validation.error) {
      alertError(validation.error);
      return Promise.reject();
    } else {
      // TODO SEND DIFERENCES ONLY
      const submitableFields = columns(user).map(prop => prop.field);
      const updatedData = {};
      submitableFields.forEach(field => {
        if (Array.isArray(oldData[field])) {
          if (JSON.stringify(newData[field]) !== JSON.stringify(oldData[field])) {
            updatedData[field] = validation[field];
          }
        } else if (newData[field] !== oldData[field]) {
          updatedData[field] = validation[field];
        }
      });
      update(newData.id, updatedData);
      return Promise.resolve();
    }
  };

  const onAdd = newData => {
    const validation = rawMaterialValidator(newData, rawMaterials);
    validation.cas = JSON.stringify(validation.cas);
    validation.ec = JSON.stringify(validation.ec);
    if (validation.confirm) {
      confirmBoxOpen({
        confirm: validation.confirm,
        onConfirmYes: () => {
          update(
            validation.oldData.id,
            { name: validation.newData.name, code: validation.newData.code }
          );
          confirmBoxClose();
        },
        onConfirmNo: () => {
          update(validation.oldData.id, { code: validation.newData.code });
          confirmBoxClose();
        }
      });
      return Promise.resolve();
    } else if (validation.error) {
      alertError(validation.error);
      return Promise.reject();
    } else {
      create(validation);
      return Promise.resolve();
    }
  };

  const onDelete = oldData => {
    const mixIds = (mixturesBySubstance[oldData.id] || []).map(
      mixtureSubstance => mixtureSubstance.mixture_id);
    // `oldData.products` was the m2m through product_raw_materials, so it held
    // one product id per composition row - repeats included.
    const productIds = (compositionByRawMaterial[oldData.id] || []).map(prm => prm.product_id);
    const productList = productIds.filter( productId => {
      const rmInUse = (compositionByProduct[productId] || []).find(prm => {
        const prmRawMaterial = rawMaterialsById[prm.raw_material_id];
        return prm.raw_material_id &&
          ( prm.raw_material_id === oldData.id || mixIds.includes(prm.raw_material_id) ) &&
          !prm.mixture_id &&
          prm.added_to_charge &&
          prmRawMaterial.code !== '' &&
          prmRawMaterial.code !== null;
      });
      return !!rmInUse;
    });

    const shipments = shipmentsOfRawMaterial[oldData.id] || [];

    if (shipments.length > 0 || productList.length > 0) {
      let actionsPending = ['Por favor apague a seguinte informação associada a esta MP:'];
      if (shipments.length > 0) {
        actionsPending.push('- Fornecimentos com registos associados.');
      }
      if (productList.length > 0) {
        const pList = productList.map(id => productsById[id].name);
        actionsPending.push('- Produtos com registos associados: ' + pList.join(', '));
      }
      alertError(actionsPending);
      return Promise.resolve();
    } else {
      if (oldData.type === MIXTURE) {
        // TODO UPDATE PRODUCTS / SHIPMENTS / RAWMATERIALS RELATED
        remove(oldData.id);
        return Promise.resolve();
      } else {
        update(oldData.id, { code: null });
        return Promise.resolve();
      }
    }
  };

  const exportPDF = () => {
    const filteredTableData = tableRef.current.state.data;
    pdfMake.createPdf(
      docDefinition(filteredTableData))
      .open();
  };

  // TODO: Test cellEditable. Should be reviewed bcause of redux SAGA modifications
  //
  // The `.finally(() => setLoading(false))` that used to close this was calling a
  // function that does not exist in this file - there is no `useState` here at
  // all. It threw `setLoading is not defined` on the one path that reaches it: a
  // cell edit whose save fails, where the catch above shows the alert and then
  // this line replaced the error with its own. Found by turning on `no-undef`,
  // which is the rule that was missing rather than the code being subtle.
  //
  // Nothing replaces it, because there was nothing to do: the spinner reads
  // `rawMaterials.loading` from the store, and the reducer already sets that flag
  // on the request and clears it on the answer, fulfilled or rejected alike.
  const onUpdateCell = (newValue, oldValue, rowData, columnDef) => {
    const oldData = rowData;
    const newData = { ...rowData, [columnDef.field]: newValue };
    return onUpdate(newData, oldData)
      .catch(e => alert(e));
  };


  return (
    <React.Fragment>
      <Grid size={12}>
        <BaseTable
          ref={tableRef}
          columns={columns(user)}
          data={filteredRawMaterials}
          onRowClick={() => null}
          isLoading={rawMaterials.loading}
          // TODO: Test cellEditable
          // cellEditable={{
          //   onCellEditApproved: onUpdateCell
          // }}
          onSearchChange={ text => setSearch(text)}
          options={{
            searchText: contextSearch,
          }}
          actions={[
            {
              isFreeAction: true,
              icon: tableIcons.Pdf,
              tooltip: 'Exportar PDF',
              onClick: exportPDF
            },
            {
              isFreeAction: true,
              icon: tableIcons.Xlsx,
              tooltip: 'Exportar XLSX',
              onClick: exportXLSX(tableRef, 'MATERIAS_PRIMAS.xlsx')
            }
          ]}
          editable={{
            onRowAdd: onAdd,
            onRowUpdate: onUpdate,
            onRowDelete: onDelete
          }}
          detailPanel={[
            rowData => rowData.type !== MIXTURE ?
              noIcon :
              {
                icon: tableIcons.Substance,
                disabled: (substancesByMixture[rowData.id] || []).length === 0,
                tooltip: 'Composição',
                render: () => <SubstancesDetailPanel rowData={substancesByMixture[rowData.id] || []} data={rawMaterialsById} title={'Substâncias'}/>
              },
            rowData =>
              ({
                icon: (shipmentsOfRawMaterial[rowData.id] || []).length > 0 ?
                  tableIcons.Shipments :
                  tableIcons.ShipmentsStyled({ htmlColor: theme.palette.error.main }),
                disabled: (shipmentsOfRawMaterial[rowData.id] || []).length === 0,
                tooltip: 'Fornecimentos',
                render: () => <ShipmentsDetailPanel rowData={rowData} suppliersById={suppliersById} rawMaterialsById={rawMaterialsById} title={'Fornecimentos'}/>
              }),
            rowData =>
              ({
                icon: (compositionByRawMaterial[rowData.id] || []).length > 0 ?
                  tableIcons.Products :
                  tableIcons.ProductsStyled({ htmlColor: theme.palette.error.main }),
                disabled: (compositionByRawMaterial[rowData.id] || []).length === 0,
                tooltip: 'Produtos',
                render: () => <RMProductsDetailPanel
                  rowData={rowData}
                  productsById={productsById}
                  rawMaterialsById={rawMaterialsById}
                  title={'Produtos'}
                />
              }),
            rowData => (consumptionsByRawMaterial[rowData.id] || []).length === 0 ?
              noIcon :
              {
                icon: tableIcons.Consumptions,
                tooltip: 'Consumos',
                render: () => <ConsumptionsDetailPanel rowData={consumptionsByRawMaterial[rowData.id] || []} data={consumptionsById} title={'Consumos'}/>
              }
          ]}
        />
      </Grid>
      {
        ui.confirmBox &&
        <ConfirmationDialog
          open={true}
          alert={ui.confirmBox.confirm}
          onConfirmYes={ui.confirmBox.onConfirmYes}
          onConfirmNo={ui.confirmBox.onConfirmNo}
          //onClose={() => setConfirmBox(null)}
          onClose={confirmBoxClose}
          backText={'Cancelar'}
          yesText={'Sobrepor'}
          noText={'Manter'}
        />
      }
    </React.Fragment>
  );
};


const makeMapStateToProps = () => {
  const filteredRawMaterials = filterByFunctionSelector('rawMaterials', rm => rm.code !== null && rm.code !== '');
  // The relations this screen used to read off the row. Indexes rather than
  // filters: each one is asked a question per rendered row, and per row of the
  // composition inside onDelete.
  const compositionByProduct = groupBy('productRawMaterials', 'product_id');
  const compositionByRawMaterial = groupBy('productRawMaterials', 'raw_material_id');
  const consumptionsByRawMaterial = groupBy('rawMaterialConsumptions', 'raw_material_id');
  const mapStateToProps = (state, props) => ({
    compositionByProduct: compositionByProduct(state),
    compositionByRawMaterial: compositionByRawMaterial(state),
    consumptionsByRawMaterial: consumptionsByRawMaterial(state),
    filteredRawMaterials: filteredRawMaterials(state),
    mixturesBySubstance: mixtureSubstancesBySubstance(state),
    shipmentsOfRawMaterial: shipmentsByRawMaterial(state),
    substancesByMixture: mixtureSubstancesByMixture(state),
    products: state.products,
    rawMaterials: state.rawMaterials.data,
    rawMaterialsById: state.rawMaterials.models,
    suppliersById: state.suppliers.models,
    productsById: state.products.models,
    consumptionsById: state.rawMaterialConsumptions.models,
    ui: state.ui.pageRM,
    contextSearch: state.ui.context.searchRM,
    user: currentUser(state)
  });
  return mapStateToProps;
};

const mapDispatchToProps = dispatch => ({
  alertError: error => dispatch(alertError(error)),
  fetch: id => dispatch(rawMaterialsActions.fetchOne(id)),
  create: data => dispatch(rawMaterialsActions.createPromise(data)),
  confirmBoxOpen: data => dispatch(confirmBoxOpen(data)),
  confirmBoxClose: () => dispatch(confirmBoxClose()),
  remove: id => dispatch(rawMaterialsActions.removePromise(id)),
  update: (id, data) => dispatch(rawMaterialsActions.updatePromise(id, data)),
  setSearch: search => dispatch(setContextSearchRM(search))
});

export default connect(makeMapStateToProps, mapDispatchToProps)(Component);
